import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom'
import Content from '@/components/specific/Content'
import { useDocumentTitle } from '@/utils/hooks/useDocumentTitle'
import numeral from 'numeral'
import global from '@/utils/helpers/global'
import { getDetailTagihanPublic } from '@/utils/apis/public'
import './infopelanggan.css'
import InfoBackground from './InfoBackground'

function DetailV2() {
  const navigate = useNavigate()
  const { search } = useLocation()
  const { id } = useParams()
  const qparams = new URLSearchParams(search)

  const history = qparams.get('history') ?? null
  const last_page = qparams.get('last_page') ?? 1

  useDocumentTitle('Detail Tagihan - Perumda Air Minum Cilegon Mandiri')

  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)

  useEffect(() => {
    const loadDetailData = async () => {
      if (!id) {
        setError('ID tagihan tidak ditemukan')
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const response = await getDetailTagihanPublic(id)

        if (response?.success && response?.data?.data) {
          setData(response.data.data)
        } else {
          setError(response?.data?.message || 'Data detail tagihan tidak ditemukan')
        }
      } catch (err) {
        console.error('Error fetching detail:', err)
        const errorMessage = err?.response?.data?.message || err?.message || 'Terjadi kesalahan saat mengambil data detail tagihan.'
        setError(errorMessage)
      } finally {
        setIsLoading(false)
      }
    }

    loadDetailData()
  }, [id])

  // Handle ESC key to close lightbox
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isLightboxOpen) {
        setIsLightboxOpen(false)
      }
    }

    if (isLightboxOpen) {
      document.addEventListener('keydown', handleEsc)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEsc)
      document.body.style.overflow = 'unset'
    }
  }, [isLightboxOpen])

  const handleBack = () => {
    navigate(`/info-tagihan${history ? `?history=${history}&last_page=${last_page}` : ''}`)
  }

  // Get status pelanggan text
  const getStatusPelangganText = (status) => {
    const statusMap = {
      'S1': 'Aktif',
      'S2': 'Di Tutup',
      'S3': 'Di Cabut',
      'S4': 'Terdaftar',
      'S5': 'Tidak Teridentifikasi'
    }
    return statusMap[status] || status || '-'
  }

  // Calculate total invoice
  const totalInvoice = data?.pergitungan_rekening?.reduce((sum, item) => sum + (item.total_harga || 0), 0) || 0

  if (isLoading) {
    return (
      <>
        <InfoBackground />
        <Content showContentHeader={false}>
          <div className="ip">
            <div className="page-hero-ip">
              <div className="wrap">
                <div className="crumbs">
                  <Link to="/info-tagihan">Info Tagihan</Link>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 6l6 6-6 6" /></svg>
                  <span>Detail</span>
                </div>
                <span className="eyebrow">Detail Rekening</span>
                <h1>Detail <span className="hl">Tagihan.</span></h1>
              </div>
            </div>
            <div className="section">
              <div className="wrap">
                <div className="state-card-ip">
                  <span className="loading loading-spinner loading-lg text-[#095393]"></span>
                  <p className="mt">Memuat detail tagihan...</p>
                </div>
              </div>
            </div>
          </div>
        </Content>
      </>
    )
  }

  if (error || !data) {
    return (
      <>
        <InfoBackground />
        <Content showContentHeader={false}>
          <div className="ip">
            <div className="page-hero-ip">
              <div className="wrap">
                <div className="crumbs">
                  <Link to="/info-tagihan">Info Tagihan</Link>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 6l6 6-6 6" /></svg>
                  <span>Detail</span>
                </div>
                <span className="eyebrow">Detail Rekening</span>
                <h1>Detail <span className="hl">Tagihan.</span></h1>
              </div>
            </div>
            <div className="section">
              <div className="wrap">
                <div className="state-card-ip">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto mb-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p style={{ fontWeight: 600, marginBottom: 8 }}>Terjadi Kesalahan</p>
                  <p>{error || 'Data detail tagihan tidak ditemukan'}</p>
                  <button
                    onClick={handleBack}
                    className="btn-ip btn-primary mt"
                  >
                    Kembali
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Content>
      </>
    )
  }

  return (
    <>
      <InfoBackground />
      <Content showContentHeader={false}>
        <div className="ip">
          <div className="page-hero-ip">
            <div className="wrap">
              <div className="crumbs">
                <Link to="/v2/info-tagihan">Info Tagihan</Link>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 6l6 6-6 6" /></svg>
                <span>Detail</span>
              </div>
              <div className="head-row">
                <div>
                  <span className="eyebrow">Detail Rekening</span>
                  <h1>
                    Tagihan{' '}
                    <span className="hl">
                      {data.informasi_tagihan?.periode_bulan
                        ? global.convert_month(data.informasi_tagihan.periode_bulan)
                        : ''}{' '}
                      {data.informasi_tagihan?.periode_tahun || ''}.
                    </span>
                  </h1>
                </div>
                <button className="btn-ip btn-outline btn-sm" onClick={handleBack}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M11 18l-6-6 6-6" /></svg>
                  Kembali
                </button>
              </div>
            </div>
          </div>

          <div className="section">
            <div className="wrap two">
              <div>
                {/* Informasi Pelanggan */}
                <div className="card card-pad">
                  <div className="card-head">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                      <circle cx="12" cy="8" r="4" />
                      <path d="M4 21a8 8 0 0116 0" />
                    </svg>
                    Informasi Pelanggan
                  </div>
                  <div className="kv">
                    <span className="k">Nama Pelanggan</span>
                    <span className="v">{data.informasi_pelanggan?.nama_pelanggan || '-'}</span>
                  </div>
                  <div className="kv">
                    <span className="k">Alamat</span>
                    <span className="v">{data.informasi_pelanggan?.alamat || '-'}</span>
                  </div>
                  <div className="kv">
                    <span className="k">Golongan</span>
                    <span className="v">
                      <span className="gol" style={{ background: 'var(--ip-soft)', color: 'var(--ip-brand-deep)' }}>
                        {data.informasi_pelanggan?.golongan || '-'}
                      </span>
                    </span>
                  </div>
                  <div className="kv">
                    <span className="k">Kabupaten / Kota</span>
                    <span className="v">{data.informasi_pelanggan?.kabupaten || '-'}</span>
                  </div>
                  <div className="kv">
                    <span className="k">Kecamatan</span>
                    <span className="v">{data.informasi_pelanggan?.kecamatan || '-'}</span>
                  </div>
                  <div className="kv">
                    <span className="k">Kelurahan</span>
                    <span className="v">{data.informasi_pelanggan?.kelurahan || '-'}</span>
                  </div>
                  {data.informasi_pelanggan?.handphone && (
                    <div className="kv">
                      <span className="k">Handphone</span>
                      <span className="v">{data.informasi_pelanggan.handphone}</span>
                    </div>
                  )}
                  <div className="kv">
                    <span className="k">Status Pelanggan</span>
                    <span className="v">
                      <span className="pill pill-green">
                        {getStatusPelangganText(data.informasi_pelanggan?.status)}
                      </span>
                    </span>
                  </div>
                  <div className="kv">
                    <span className="k">Status Tagihan</span>
                    <span className="v">
                      {data.informasi_tagihan?.status_text === 'Lunas' ? (
                        <span className="pill pill-green">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"><path d="M20 6L9 17l-5-5" /></svg>
                          {data.informasi_tagihan.status_text}
                        </span>
                      ) : (
                        <span className="pill pill-red">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M18 6L6 18M6 6l12 12" /></svg>
                          {data.informasi_tagihan?.status_text || 'Belum Lunas'}
                        </span>
                      )}
                    </span>
                  </div>
                </div>

                {/* Periode Tagihan */}
                <div className="card card-pad mt">
                  <div className="card-head">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                      <rect x="3" y="4" width="18" height="18" rx="2" />
                      <path d="M16 2v4M8 2v4M3 10h18" />
                    </svg>
                    Periode Tagihan · {data.informasi_tagihan?.periode_bulan ? global.convert_month(data.informasi_tagihan.periode_bulan) : '-'} {data.informasi_tagihan?.periode_tahun || ''}
                  </div>
                  <div className="meter">
                    <div className="mc">
                      <div className="k">Stan Bulan Lalu</div>
                      <div className="v">{numeral(data.informasi_meter?.m3_awal || 0).format('0,0')}</div>
                    </div>
                    <div className="mc">
                      <div className="k">Stan Bulan Ini</div>
                      <div className="v">{numeral(data.informasi_meter?.m3_akhir || 0).format('0,0')}</div>
                    </div>
                    <div className="mc hl">
                      <div className="k">Pemakaian</div>
                      <div className="v">{numeral(data.informasi_meter?.m3_pemakaian || 0).format('0,0')} <small>m³</small></div>
                    </div>
                  </div>
                </div>

                {/* Perhitungan Rekening */}
                <div className="card card-pad mt">
                  <div className="card-head">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                      <path d="M9 7h6M9 11h6M9 15h4M6 3h9l4 4v14H6z" />
                    </svg>
                    Perhitungan Rekening
                  </div>
                  <div className="break" style={{ border: 0, padding: 0 }}>
                    {data.pergitungan_rekening?.map((item, idx) => (
                      <div className="r" key={idx}>
                        <span>{item.nama_produk}</span>
                        <span>Rp. {numeral(item.total_harga || 0).format('0,0')}</span>
                      </div>
                    ))}
                    <div className="r total">
                      <span>Total Tagihan</span>
                      <b>Rp. {numeral(totalInvoice).format('0,0')}</b>
                    </div>
                  </div>
                </div>
              </div>

              {/* Foto Meter */}
              <div>
                <div className="card card-pad" style={{ position: 'sticky', top: 88 }}>
                  <div className="card-head">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                      <rect x="3" y="5" width="18" height="14" rx="2" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                    Foto Meter
                  </div>
                  {data.informasi_meter?.foto_meter ? (
                    <div className="fotometer" onClick={() => setIsLightboxOpen(true)}>
                      <img
                        src={data.informasi_meter.foto_meter}
                        alt="Foto Meter"
                        onError={(e) => {
                          e.target.onerror = null
                          e.target.src = `${process.env.PUBLIC_URL}/noimage.png`
                        }}
                      />
                      <span className="zoom">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4M11 8v6M8 11h6" /></svg>
                        Perbesar
                      </span>
                    </div>
                  ) : (
                    <div className="fotometer" style={{ cursor: 'default' }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
                        <rect x="3" y="5" width="18" height="14" rx="2" />
                        <circle cx="12" cy="12" r="3.4" />
                        <path d="M7 5V3M17 5V3" />
                      </svg>
                    </div>
                  )}
                  <p style={{ fontSize: 12.5, color: 'var(--ip-ink-3)', marginTop: 14, textAlign: 'center' }}>
                    {data.informasi_meter?.foto_meter
                      ? 'Foto pencatatan meter periode ini. Klik untuk memperbesar.'
                      : 'Foto meter tidak tersedia.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Content>

      {/* Lightbox - Simple */}
      {isLightboxOpen && data.informasi_meter?.foto_meter && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 p-4"
          onClick={() => setIsLightboxOpen(false)}
        >
          {/* Close Button */}
          <button
            onClick={(e) => {
              e.stopPropagation()
              setIsLightboxOpen(false)
            }}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10 bg-black/50 rounded-full p-2 hover:bg-black/70"
            aria-label="Tutup"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Image Container */}
          <div className="relative max-w-7xl max-h-[95vh] w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={data.informasi_meter.foto_meter}
              alt="Foto Meter"
              className="w-full h-auto max-h-[95vh] object-contain rounded-lg shadow-2xl"
              onError={(e) => {
                e.target.onerror = null
                e.target.src = `${process.env.PUBLIC_URL}/noimage.png`
              }}
            />
          </div>
        </div>
      )}
    </>
  )
}

export default DetailV2
